import {
  pgTable,
  text,
  boolean,
  timestamp,
  integer,
  index,
  uniqueIndex,
  foreignKey,
  jsonb,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import {
  devicePlatform,
  deviceStatus,
  keyBackupStatus,
  resetCodeChannel,
} from './enums';
import { user } from './user';
import { chatRoom, chatMessage } from './messaging';

// ==========================================
// USER IDENTITY — основная Olm identity юзера + passphrase-backup root key
// ==========================================
// Один идентити на юзера. Хранит публичный Olm identity (Curve25519/Ed25519
// — JSON в `publicKey`), а также данные для passphrase-based recovery:
// `passphraseSalt` для Argon2id, `passphraseVerifier` для проверки пароля
// без его раскрытия серверу, `wrappedRootKey` — root key, обёрнутый
// passphrase-derived AES-GCM ключом, и `recoveryCodeHash` — хэш аварийного
// recovery-кода (если passphrase утерян).
export const userIdentity = pgTable(
  'UserIdentity',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    userId: integer().notNull(),
    publicKey: text().notNull(),
    // Legacy passphrase-based backup поля. С переходом на PIN-only flow
    // (Task 22+) не заполняются новыми identity и не читаются клиентом.
    // Оставлены nullable для совместимости с существующими строками —
    // физически удалены не будут, чтобы не потерять recovery для уже
    // созданных до миграции identity.
    passphraseSalt: text(),
    passphraseVerifier: text(),
    wrappedRootKey: text(),
    recoveryCodeHash: text(),
    // ==========================================
    // PIN-based auth (Task 21+)
    // ==========================================
    // Новая модель: юзер вводит 6+ цифр PIN, клиент деривирует
    // pin-derived AES-GCM ключ через Argon2id(pinSalt). `pinVerifier` —
    // отдельный Argon2id-хэш для server-side проверки без раскрытия PIN.
    // `wrappedRootKeyByPin` — root key, обёрнутый pin-derived ключом.
    // Существующие passphrase-поля остаются (legacy, удаляются в Task 25
    // после миграции активных пользователей).
    pinSalt: text(),
    pinVerifier: text(),
    wrappedRootKeyByPin: text(),
    // ==========================================
    // Reset flow (сброс PIN через EMAIL/TELEGRAM код)
    // ==========================================
    // `wrappedRootKeyByResetCode` — вторая копия root key, обёрнутая
    // через ключ, деривированный из долгоживущего reset-кода (не путать
    // с одноразовым 6-значным challenge в `ResetCodeAttempt`). Это
    // позволяет восстановить root key (и, значит, историю) после сброса
    // PIN — юзер задаёт новый PIN, root key переобёртывается под него.
    // `resetCodeSalt` / `resetCodeVerifier` — Argon2id-параметры для
    // проверки reset-кода на сервере. `resetCodeChannel` — выбранный
    // юзером канал доставки challenge-кода (EMAIL/TELEGRAM).
    wrappedRootKeyByResetCode: text(),
    resetCodeSalt: text(),
    resetCodeVerifier: text(),
    resetCodeChannel: resetCodeChannel(),
    // `resetBackupKeyEncrypted` — долгоживущий reset-backup-key клиента,
    // зашифрованный серверным MASTER_KEY (AES-256-GCM). Хранится в формате
    // JSON `{"iv":"<b64>","ct":"<b64>","tag":"<b64>"}`. При успешной
    // проверке reset-кода сервер расшифровывает и возвращает клиенту.
    resetBackupKeyEncrypted: text(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    uniqueIndex('UserIdentity_userId_key').using('btree', table.userId.asc().nullsLast()),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'UserIdentity_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// ==========================================
// USER DEVICE — каждое устройство юзера со своим Olm device key
// ==========================================
// `wrappedDeviceKey` — приватный Olm device key, обёрнутый rootKey.
// `deviceId` — Olm device id (генерируется клиентом). `devicePublicKey`
// — JSON с Curve25519 + Ed25519 публичными ключами устройства.
export const userDevice = pgTable(
  'UserDevice',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    userId: integer().notNull(),
    identityId: integer().notNull(),
    deviceId: text().notNull(),
    devicePublicKey: text().notNull(),
    wrappedDeviceKey: text().notNull(),
    label: text(),
    platform: devicePlatform(),
    status: deviceStatus().default('ACTIVE').notNull(),
    lastSeen: timestamp({ precision: 3, mode: 'date' }),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    uniqueIndex('UserDevice_userId_deviceId_key').using(
      'btree',
      table.userId.asc().nullsLast(),
      table.deviceId.asc().nullsLast(),
    ),
    index('UserDevice_userId_idx').using('btree', table.userId.asc().nullsLast()),
    index('UserDevice_identityId_idx').using('btree', table.identityId.asc().nullsLast()),
    index('UserDevice_status_idx').using('btree', table.status.asc().nullsLast()),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'UserDevice_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.identityId],
      foreignColumns: [userIdentity.id],
      name: 'UserDevice_identityId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// ==========================================
// DEVICE ONE-TIME KEY — prekey-bundle для X3DH-like handshake
// ==========================================
// При establishement новой 1:1 Olm-сессии отправитель забирает один OTK
// получателя, помечает `consumed=true`, дальше ключ нельзя использовать
// повторно. Сервер только хранит публичные ключи, приватные — на устройстве.
export const deviceOneTimeKey = pgTable(
  'DeviceOneTimeKey',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    deviceId: integer().notNull(),
    keyId: text().notNull(),
    publicKey: text().notNull(),
    consumed: boolean().default(false).notNull(),
    consumedAt: timestamp({ precision: 3, mode: 'date' }),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    index('DeviceOneTimeKey_deviceId_consumed_idx').using(
      'btree',
      table.deviceId.asc().nullsLast(),
      table.consumed.asc().nullsLast(),
    ),
    uniqueIndex('DeviceOneTimeKey_deviceId_keyId_key').using(
      'btree',
      table.deviceId.asc().nullsLast(),
      table.keyId.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.deviceId],
      foreignColumns: [userDevice.id],
      name: 'DeviceOneTimeKey_deviceId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// ==========================================
// OLM SESSION — метаданные 1:1 Double Ratchet сессии
// ==========================================
// Реальное состояние ratchet-а живёт у клиента (matrix-sdk-crypto-wasm).
// Сервер хранит только public-references: чтобы узнать "есть ли сессия
// между моим устройством X и устройством Y" и для отладки.
export const olmSession = pgTable(
  'OlmSession',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    ownerDeviceId: integer().notNull(),
    peerDeviceId: integer().notNull(),
    sessionId: text().notNull(),
    lastUsedAt: timestamp({ precision: 3, mode: 'date' }),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    uniqueIndex('OlmSession_owner_peer_session_key').using(
      'btree',
      table.ownerDeviceId.asc().nullsLast(),
      table.peerDeviceId.asc().nullsLast(),
      table.sessionId.asc().nullsLast(),
    ),
    index('OlmSession_ownerDeviceId_idx').using('btree', table.ownerDeviceId.asc().nullsLast()),
    index('OlmSession_peerDeviceId_idx').using('btree', table.peerDeviceId.asc().nullsLast()),
    foreignKey({
      columns: [table.ownerDeviceId],
      foreignColumns: [userDevice.id],
      name: 'OlmSession_ownerDeviceId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.peerDeviceId],
      foreignColumns: [userDevice.id],
      name: 'OlmSession_peerDeviceId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// ==========================================
// MEGOLM GROUP SESSION — метаданные Megolm-сессии для групп
// ==========================================
// Megolm = одна симметричная "outbound" ratchet-сессия на отправителя в
// группе. `epoch` инкрементируется при ротации (например, при изменении
// состава участников). Реальные ключи sender'а — у клиента; сервер видит
// только session_id и owner.
export const megolmGroupSession = pgTable(
  'MegolmGroupSession',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    chatRoomId: integer().notNull(),
    senderDeviceId: integer().notNull(),
    sessionId: text().notNull(),
    epoch: integer().default(0).notNull(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    uniqueIndex('MegolmGroupSession_room_sender_session_key').using(
      'btree',
      table.chatRoomId.asc().nullsLast(),
      table.senderDeviceId.asc().nullsLast(),
      table.sessionId.asc().nullsLast(),
    ),
    index('MegolmGroupSession_chatRoomId_idx').using('btree', table.chatRoomId.asc().nullsLast()),
    index('MegolmGroupSession_senderDeviceId_idx').using(
      'btree',
      table.senderDeviceId.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.chatRoomId],
      foreignColumns: [chatRoom.id],
      name: 'MegolmGroupSession_chatRoomId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.senderDeviceId],
      foreignColumns: [userDevice.id],
      name: 'MegolmGroupSession_senderDeviceId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// ==========================================
// ENCRYPTED MESSAGE — Megolm-ciphertext для сообщения в E2EE-комнате
// ==========================================
// `chatMessage` хранит метаданные (senderId, chatRoomId, createdAt, replyTo,
// isDeleted и т.д.), но `content` в E2EE-комнатах НЕ заполняется реальным
// текстом (service layer пишет туда пустую строку или маркер). Реальный
// ciphertext лежит здесь.
export const encryptedMessage = pgTable(
  'EncryptedMessage',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    chatMessageId: integer().notNull(),
    senderDeviceId: integer().notNull(),
    ciphertext: text().notNull(),
    sessionId: text().notNull(),
    messageIndex: integer().notNull(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    uniqueIndex('EncryptedMessage_chatMessageId_key').using(
      'btree',
      table.chatMessageId.asc().nullsLast(),
    ),
    index('EncryptedMessage_senderDeviceId_idx').using(
      'btree',
      table.senderDeviceId.asc().nullsLast(),
    ),
    index('EncryptedMessage_sessionId_idx').using('btree', table.sessionId.asc().nullsLast()),
    foreignKey({
      columns: [table.chatMessageId],
      foreignColumns: [chatMessage.id],
      name: 'EncryptedMessage_chatMessageId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.senderDeviceId],
      foreignColumns: [userDevice.id],
      name: 'EncryptedMessage_senderDeviceId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// ==========================================
// ENCRYPTED HISTORY BLOB — per-device копии для multi-device sync
// ==========================================
// iMessage-way: при отправке сообщения отправитель шифрует копию payload-а
// под device-key КАЖДОГО своего активного устройства, чтобы при логине
// нового устройства можно было восстановить историю без обращения к
// peer-у. Также используется для key-share: когда новое устройство юзера
// X запрашивает megolm session_key — отправитель видит его devicePublicKey
// и кладёт ciphertext сюда.
export const encryptedHistoryBlob = pgTable(
  'EncryptedHistoryBlob',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    chatMessageId: integer().notNull(),
    targetDeviceId: integer().notNull(),
    ciphertext: text().notNull(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    index('EncryptedHistoryBlob_targetDevice_message_idx').using(
      'btree',
      table.targetDeviceId.asc().nullsLast(),
      table.chatMessageId.asc().nullsLast(),
    ),
    uniqueIndex('EncryptedHistoryBlob_message_target_key').using(
      'btree',
      table.chatMessageId.asc().nullsLast(),
      table.targetDeviceId.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.chatMessageId],
      foreignColumns: [chatMessage.id],
      name: 'EncryptedHistoryBlob_chatMessageId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.targetDeviceId],
      foreignColumns: [userDevice.id],
      name: 'EncryptedHistoryBlob_targetDeviceId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// ==========================================
// KEY BACKUP — secret-storage бэкапы Megolm-сессий
// ==========================================
// Совместимо с Matrix m.megolm_backup.v1.curve25519-aes-sha2:
// клиент генерирует backup keypair, публичный кладёт в `authData`, приватный
// шифрует под root key пользователя и хранит локально/в secret-storage.
// Backups позволяют восстановить historic Megolm-сессии при логине нового
// устройства БЕЗ запроса у peer-ов.
export const keyBackup = pgTable(
  'KeyBackup',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    userId: integer().notNull(),
    version: integer().notNull(),
    algorithm: text().notNull(),
    authData: text().notNull(),
    status: keyBackupStatus().default('ACTIVE').notNull(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    uniqueIndex('KeyBackup_userId_version_key').using(
      'btree',
      table.userId.asc().nullsLast(),
      table.version.asc().nullsLast(),
    ),
    index('KeyBackup_userId_status_idx').using(
      'btree',
      table.userId.asc().nullsLast(),
      table.status.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'KeyBackup_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// ==========================================
// KEY BACKUP SESSION — одна Megolm-сессия в бэкапе
// ==========================================
export const keyBackupSession = pgTable(
  'KeyBackupSession',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    backupId: integer().notNull(),
    chatRoomId: integer().notNull(),
    sessionId: text().notNull(),
    encryptedSession: text().notNull(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    uniqueIndex('KeyBackupSession_backup_room_session_key').using(
      'btree',
      table.backupId.asc().nullsLast(),
      table.chatRoomId.asc().nullsLast(),
      table.sessionId.asc().nullsLast(),
    ),
    index('KeyBackupSession_backupId_idx').using('btree', table.backupId.asc().nullsLast()),
    foreignKey({
      columns: [table.backupId],
      foreignColumns: [keyBackup.id],
      name: 'KeyBackupSession_backupId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.chatRoomId],
      foreignColumns: [chatRoom.id],
      name: 'KeyBackupSession_chatRoomId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// ==========================================
// MATRIX TO-DEVICE EVENT — очередь to-device событий для доставки по WS
// ==========================================
// Поддерживает Matrix CSAPI `PUT /sendToDevice/{eventType}/{txnId}`. Событие
// создаётся при вызове API и доставляется адресату через существующий
// chat WebSocket (push в `pushToDevice`). `deliveredAt` заполняется, когда
// клиент явно подтвердил доставку (`matrix.to_device.ack`). На reconnect
// сервер флашит pending-события для устройства — это даёт at-least-once.
// Уникальность `(senderDeviceId, txnId, targetDeviceId)` обеспечивает
// идемпотентность повторного API-вызова (в том числе для broadcast "*",
// где один txnId разворачивается в N строк под каждое устройство).
// TODO: periodic GC для старых delivered событий (вне MVP).
export const matrixToDeviceEvent = pgTable(
  'MatrixToDeviceEvent',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    targetDeviceId: integer().notNull(),
    senderUserId: integer().notNull(),
    senderDeviceId: integer().notNull(),
    eventType: text().notNull(),
    content: jsonb().notNull(),
    txnId: text().notNull(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    deliveredAt: timestamp({ precision: 3, mode: 'date' }),
  },
  (table) => [
    index('MatrixToDeviceEvent_target_delivered_idx').using(
      'btree',
      table.targetDeviceId.asc().nullsLast(),
      table.deliveredAt.asc().nullsLast(),
    ),
    uniqueIndex('MatrixToDeviceEvent_sender_txn_target_key').using(
      'btree',
      table.senderDeviceId.asc().nullsLast(),
      table.txnId.asc().nullsLast(),
      table.targetDeviceId.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.targetDeviceId],
      foreignColumns: [userDevice.id],
      name: 'MatrixToDeviceEvent_targetDeviceId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.senderUserId],
      foreignColumns: [user.id],
      name: 'MatrixToDeviceEvent_senderUserId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.senderDeviceId],
      foreignColumns: [userDevice.id],
      name: 'MatrixToDeviceEvent_senderDeviceId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// ==========================================
// ENCRYPTED MEDIA — зашифрованные файлы в E2EE-чатах
// ==========================================
// Файл шифруется клиентом перед upload-ом, ciphertext кладётся в S3/MinIO
// под `blobUrl`. Симметричный ключ файла кладётся в payload сообщения
// (внутри Megolm ciphertext) и помечен `keyRefInMessage` — это id ключа
// внутри JSON payload-а сообщения. Сервер не видит ни plaintext, ни ключ.
export const encryptedMedia = pgTable(
  'EncryptedMedia',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    chatMessageId: integer().notNull(),
    blobUrl: text().notNull(),
    mimeType: text().notNull(),
    sizeBytes: integer().notNull(),
    keyRefInMessage: text().notNull(),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  },
  (table) => [
    index('EncryptedMedia_chatMessageId_idx').using(
      'btree',
      table.chatMessageId.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.chatMessageId],
      foreignColumns: [chatMessage.id],
      name: 'EncryptedMedia_chatMessageId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// ==========================================
// RESET CODE ATTEMPT — один выданный 6-значный challenge reset-кода
// ==========================================
// При запросе reset-flow сервер генерирует 6-значный код, хранит его
// Argon2id-хэш (быстрый, low-memory — код одноразовый, защищает от
// online-брутфорса rate-limit-ом, а не вычислительной стоимостью),
// рассылает plaintext в выбранный канал (`channel` + `sentTo`).
// TTL ~10 минут, `expiresAt` отсекает истёкшие строки. `consumedAt`
// проставляется после успешного использования — повторное использование
// отклоняется. Rate-limit политика (Task 22): максимум N выданных
// кодов за окно T; хранение истории нужно для аудита.
export const resetCodeAttempt = pgTable(
  'ResetCodeAttempt',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    userId: integer().notNull(),
    codeHash: text().notNull(),
    // `codeSalt` — 16-байтная соль в base64. Вместе с MASTER_KEY формирует
    // HMAC-SHA256(MASTER_KEY, codeSalt || code), результат = `codeHash`.
    codeSalt: text().notNull().default(''),
    channel: resetCodeChannel().notNull(),
    sentTo: text().notNull(),
    expiresAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
    consumedAt: timestamp({ precision: 3, mode: 'date' }),
    // Счётчик неверных verify-попыток на этот выданный код.
    // При достижении 5 attempt помечается consumed, нужен новый request.
    failedAttempts: integer().notNull().default(0),
    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    index('ResetCodeAttempt_userId_expiresAt_idx').using(
      'btree',
      table.userId.asc().nullsLast(),
      table.expiresAt.asc().nullsLast(),
    ),
    index('ResetCodeAttempt_codeHash_idx').using('btree', table.codeHash.asc().nullsLast()),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'ResetCodeAttempt_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

// ==========================================
// PIN UNLOCK ATTEMPT — аудит попыток ввода PIN для rate-limit
// ==========================================
// Каждая попытка unlock-а PIN (успешная или нет) пишется сюда. Rate-limit
// policy (Task 22): 5 failed attempts за 15 минут → 429 Too Many Requests
// на 1 час. Index по (userId, attemptAt) обеспечивает быстрый COUNT(*)
// по окну. Успешные тоже пишем — для аудита безопасности и отображения
// "последний вход" в профиле.
export const pinUnlockAttempt = pgTable(
  'PinUnlockAttempt',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),
    userId: integer().notNull(),
    success: boolean().notNull(),
    attemptAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    index('PinUnlockAttempt_userId_attemptAt_idx').using(
      'btree',
      table.userId.asc().nullsLast(),
      table.attemptAt.asc().nullsLast(),
    ),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'PinUnlockAttempt_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);
